import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router";

import { TRACKER_ENABLED } from "../config/navigation";
// Builder redesign loader stays eager because react-router needs the function reference at config time.
import { builderRedesignLoader } from "../pages/builder/builder-redesign";
import { HomeLayout } from "../pages/home/layout";
import { AboutPage } from "../pages/public/about-page";
import { ContactPage } from "../pages/public/contact-page";
import { ErrorPage } from "../pages/public/error";
import { HelpPage } from "../pages/public/help-page";
import { HomePage } from "../pages/public/home-page";
import { publicLoader, PublicResumePage } from "../pages/public/page";
import { PricingPage } from "../pages/public/pricing-page";
import { PrivacyPage } from "../pages/public/privacy-page";
import { RefundPage } from "../pages/public/refund-page";
import { SecurityPage } from "../pages/public/security-page";
import { SecurityResearchersPage } from "../pages/public/security-researchers-page";
import { TermsPage } from "../pages/public/terms-page";
import { AtsCheckerPage } from "../pages/tools/ats-checker/page";
import { LinkedInEndorsementPage } from "../pages/tools/linkedin-endorsement/page";
import { LinkedInProfileReviewPage } from "../pages/tools/linkedin-profile-review/page";
import { ToolsPage } from "../pages/tools/page";
import { ResumeRoastPage } from "../pages/tools/resume-roast/page";
import { Providers } from "../providers";
import { AdminGuard } from "./guards/admin";
import { AdminObservabilityGuard } from "./guards/admin-observability";
import { AuthGuard } from "./guards/auth";
import { FeatureGuard } from "./guards/feature";
import { GuestGuard } from "./guards/guest";
import { lazyRoute } from "./lazy";
import { authLoader } from "./loaders/auth";

/* eslint-disable lingui/no-unlocalized-strings -- the second argument is
   the JS export name, never user-facing copy. */
// ── Lazy non-marketing routes — perf audit items #6 + #19 ──
// Everything below ships as its own bundle chunk. The marketing homepage no
// longer pulls in dashboard / settings / onboarding / auth / builder code on
// first paint; ~427 KB of unused JS dropped from the / bundle.
// Suspense boundary lives at the top of <Providers>.
const AdminLayout = lazyRoute(() => import("../pages/admin/layout"), "AdminLayout");
const FeedbackAdminPage = lazyRoute(
  () => import("../pages/dashboard/feedback/page"),
  "FeedbackAdminPage",
);
const MyReportsPage = lazyRoute(
  () => import("../pages/dashboard/feedback/my-reports"),
  "MyReportsPage",
);
const ApplicationsPage = lazyRoute(() => import("../pages/applications/page"), "ApplicationsPage");
const ApplicationTrackerPage = lazyRoute(
  () => import("../pages/applications/tracker"),
  "ApplicationTrackerPage",
);
const ActivityPage = lazyRoute(() => import("../pages/auto-apply/activity"), "ActivityPage");
const AutoApplyPage = lazyRoute(() => import("../pages/auto-apply/page"), "AutoApplyPage");
const AuthLayout = lazyRoute(() => import("../pages/auth/layout"), "AuthLayout");
const BackupOtpPage = lazyRoute(() => import("../pages/auth/backup-otp/page"), "BackupOtpPage");
const BillingReturnPage = lazyRoute(
  () => import("../pages/billing/return/page"),
  "BillingReturnPage",
);
const UpgradePage = lazyRoute(() => import("../pages/billing/upgrade/page"), "UpgradePage");
const BuilderRedesignPage = lazyRoute(
  () => import("../pages/builder/builder-redesign"),
  "BuilderRedesignPage",
);
const BuilderIndexPage = lazyRoute(() => import("../pages/builder/page"), "BuilderIndexPage");
const ChangelogPage = lazyRoute(() => import("../pages/public/changelog-page"), "ChangelogPage");
const DashboardHomePage = lazyRoute(() => import("../pages/dashboard/page"), "DashboardHomePage");
const DashboardLayout = lazyRoute(() => import("../pages/dashboard/layout"), "DashboardLayout");
const ForgotPasswordPage = lazyRoute(
  () => import("../pages/auth/forgot-password/page"),
  "ForgotPasswordPage",
);
const JobProfilesPage = lazyRoute(() => import("../pages/job-profile/page"), "JobProfilesPage");
const JobsPage = lazyRoute(() => import("../pages/jobs/index"), "JobsPage");
const TailorDeepLinkPage = lazyRoute(
  () => import("../pages/jobs/tailor-deep-link"),
  "TailorDeepLinkPage",
);
const LinkedInCookieHelpPage = lazyRoute(
  () => import("../pages/help/linkedin-cookie/page"),
  "LinkedInCookieHelpPage",
);
const LoginPage = lazyRoute(() => import("../pages/auth/login/page"), "LoginPage");
const OnboardingPage = lazyRoute(() => import("../pages/onboarding"), "OnboardingPage");
const ReferralsPage = lazyRoute(() => import("../pages/dashboard/referrals/page"), "ReferralsPage");
const RegisterPage = lazyRoute(() => import("../pages/auth/register/page"), "RegisterPage");
const ResetPasswordPage = lazyRoute(
  () => import("../pages/auth/reset-password/page"),
  "ResetPasswordPage",
);
const ResumesPage = lazyRoute(() => import("../pages/dashboard/resumes/page"), "ResumesPage");
const ApplicationProfilePage = lazyRoute(
  () => import("../pages/settings/application-profile/page"),
  "ApplicationProfilePage",
);
const NeedsInputPage = lazyRoute(() => import("../pages/needs-input/page"), "NeedsInputPage");
const NewSettingsPage = lazyRoute(() => import("../pages/settings/page"), "NewSettingsPage");
const StatsPage = lazyRoute(() => import("../pages/stats/page"), "StatsPage");
const TemplatesPage = lazyRoute(() => import("../pages/templates/page"), "TemplatesPage");
const VerifyEmailPage = lazyRoute(
  () => import("../pages/auth/verify-email/page"),
  "VerifyEmailPage",
);
const VerifyOtpPage = lazyRoute(() => import("../pages/auth/verify-otp/page"), "VerifyOtpPage");
const AdminObservabilityDashboard = lazyRoute(
  () => import("../pages/admin/_pages/observability-dashboard"),
  "AdminObservabilityDashboard",
);
const AdminScoringHealthPage = lazyRoute(
  () => import("../pages/admin/_pages/scoring-health"),
  "AdminScoringHealthPage",
);
const AdminUserAnalyticsDashboard = lazyRoute(
  () => import("../pages/admin/_pages/user-analytics-dashboard"),
  "AdminUserAnalyticsDashboard",
);
const AdminSettingsPage = lazyRoute(
  () => import("../pages/admin/_pages/settings-page"),
  "AdminSettingsPage",
);
const AdminProfileDetailPage = lazyRoute(
  () => import("../pages/admin/_pages/profile-detail"),
  "AdminProfileDetailPage",
);
const AdminApplicationTracePage = lazyRoute(
  () => import("../pages/admin/_pages/application-trace"),
  "AdminApplicationTracePage",
);
/* eslint-enable lingui/no-unlocalized-strings */

export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route errorElement={<ErrorPage />}>
      <Route element={<HomeLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="refund-policy" element={<RefundPage />} />
        <Route path="refund" element={<RefundPage />} />
        <Route path="help/linkedin-cookie" element={<LinkedInCookieHelpPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="changelog" element={<ChangelogPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="security/researchers" element={<SecurityResearchersPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="billing/return" element={<BillingReturnPage />} />
        <Route path="tools" element={<ToolsPage />} />
        <Route path="tools/ats-checker" element={<AtsCheckerPage />} />
        <Route path="tools/linkedin-endorsement" element={<LinkedInEndorsementPage />} />
        <Route path="tools/linkedin-profile-review" element={<LinkedInProfileReviewPage />} />
        <Route path="tools/resume-roast" element={<ResumeRoastPage />} />
      </Route>

      <Route path="auth">
        <Route element={<AuthLayout />}>
          <Route element={<GuestGuard />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
          </Route>

          {/* Password Recovery */}
          <Route element={<GuestGuard />}>
            <Route
              path="forgot-password"
              element={<ForgotPasswordPage />}
              errorElement={<ErrorPage />}
            />
            <Route path="reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Two-Factor Authentication */}
          <Route element={<GuestGuard />}>
            <Route path="verify-otp" element={<VerifyOtpPage />} />
            <Route path="backup-otp" element={<BackupOtpPage />} />
          </Route>

          {/* Email Verification */}
          <Route element={<AuthGuard />}>
            <Route path="verify-email" element={<VerifyEmailPage />} />
          </Route>

          {/* OAuth Callback */}
          <Route
            path="callback"
            loader={authLoader}
            element={<div />}
            errorElement={<ErrorPage />}
          />
        </Route>

        <Route index element={<Navigate replace to="/auth/login" />} />
        <Route path="*" element={<Navigate replace to="/auth/login" />} />
      </Route>

      {/* Onboarding wizard — authenticated, shown before app shell */}
      <Route element={<AuthGuard />}>
        <Route path="upgrade" element={<UpgradePage />} />
      </Route>

      <Route path="onboarding">
        <Route element={<AuthGuard />}>
          <Route index element={<OnboardingPage />} />
        </Route>
      </Route>

      <Route path="dashboard">
        <Route element={<AuthGuard />}>
          <Route element={<DashboardLayout />}>
            <Route path="resumes" element={<ResumesPage />} />
            <Route path="settings" element={<Navigate replace to="/settings" />} />
            <Route path="referrals" element={<ReferralsPage />} />
            <Route path="feedback" element={<MyReportsPage />} />
            <Route element={<AdminGuard />}>
              <Route path="admin/feedback" element={<FeedbackAdminPage />} />
            </Route>

            <Route index element={<DashboardHomePage />} />
          </Route>
        </Route>
      </Route>

      {/* Legacy /app/* redirects — kept so stale bookmarks survive. */}
      <Route path="app" element={<Navigate replace to="/dashboard" />} />
      <Route path="app/dashboard" element={<Navigate replace to="/dashboard" />} />
      <Route path="app/stats" element={<Navigate replace to="/stats" />} />
      <Route path="app/settings" element={<Navigate replace to="/settings" />} />
      <Route path="app/resumes" element={<Navigate replace to="/dashboard/resumes" />} />
      <Route path="app/jobs" element={<Navigate replace to="/jobs" />} />
      <Route path="app/applications" element={<Navigate replace to="/applications" />} />
      <Route
        path="app/applications/tracker"
        element={<Navigate replace to="/applications/tracker" />}
      />
      <Route path="app/auto-apply" element={<Navigate replace to="/auto-apply" />} />

      <Route element={<AuthGuard />}>
        <Route element={<FeatureGuard />}>
          <Route element={<DashboardLayout />}>
            <Route path="stats" element={<StatsPage />} />
            <Route path="settings" element={<NewSettingsPage />} />
            <Route path="job-profile" element={<JobProfilesPage />} />
            <Route path="job-profile/create" element={<JobProfilesPage />} />
            <Route path="job-profile/:id/edit" element={<JobProfilesPage />} />
            <Route path="jobs" element={<JobsPage />} />
            {/* Weekly-matches email: "Tailor my CV for this job" deep link. */}
            <Route path="jobs/tailor/:jobId" element={<TailorDeepLinkPage />} />
            <Route path="settings/application-profile" element={<ApplicationProfilePage />} />
            {/* /settings/auto-apply was folded into /auto-apply. Redirect
                preserves old links + bookmarks. */}
            <Route path="settings/auto-apply" element={<Navigate replace to="/auto-apply" />} />
            {/* /settings/billing has never existed as a route, yet EIGHT live
                references point at it — including BOTH trial emails
                (trial.service.ts:260,278), the locked-matches upsell on the
                jobs feed, the tailoring-limit dialog and three topbar links.
                Every one rendered "Lost at sea" (verified on prod 2026-08-09,
                with 40 trials expiring inside ten days). Billing lives in the
                #billing section of the single settings page, which already
                hash-scrolls (settings-content.tsx:180-187,240). */}
            <Route path="settings/billing" element={<Navigate replace to="/settings#billing" />} />
            <Route path="needs-input" element={<NeedsInputPage />} />
            <Route path="auto-apply" element={<AutoApplyPage />} />
            <Route path="auto-apply/activity" element={<ActivityPage />} />
            <Route path="applications" element={<ApplicationsPage />} />
            {/* Tracker hidden for all users — redirect to Applications while
                TRACKER_ENABLED is false (see config/navigation.ts). */}
            <Route
              path="applications/tracker"
              element={
                TRACKER_ENABLED ? (
                  <ApplicationTrackerPage />
                ) : (
                  <Navigate replace to="/applications" />
                )
              }
            />
          </Route>
        </Route>
      </Route>

      <Route path="builder">
        <Route element={<AuthGuard />}>
          <Route path=":id" loader={builderRedesignLoader} element={<BuilderRedesignPage />} />
          <Route index element={<BuilderIndexPage />} />
        </Route>
      </Route>

      <Route path="admin">
        <Route element={<AuthGuard />}>
          <Route element={<AdminGuard />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate replace to="/dashboard/resumes" />} />
            </Route>
          </Route>
          <Route element={<AdminObservabilityGuard />}>
            <Route element={<DashboardLayout />}>
              <Route path="observability" element={<AdminObservabilityDashboard />} />
              <Route path="scoring-health" element={<AdminScoringHealthPage />} />
              <Route path="user-analytics" element={<AdminUserAnalyticsDashboard />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="profiles/:id" element={<AdminProfileDetailPage />} />
              <Route path="applications/:id/trace" element={<AdminApplicationTracePage />} />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Public Routes */}
      <Route path=":username">
        <Route
          index
          loader={() => {
            /* eslint-disable-next-line @typescript-eslint/only-throw-error, lingui/no-unlocalized-strings -- internal HTTP plumbing */
            throw new Response("Not Found", { status: 404, statusText: "Not Found" });
          }}
        />
        <Route path=":slug" loader={publicLoader} element={<PublicResumePage />} />
      </Route>

      {/* Catch-all: surface a "Lost at sea" 404 via the existing ErrorPage
          boundary instead of rendering an empty document. React-router's
          recommended pattern is to throw a Response from a loader — the
          ErrorPage boundary reads the status and shows the 404 copy. */}
      <Route
        path="*"
        loader={() => {
          /* eslint-disable-next-line @typescript-eslint/only-throw-error, lingui/no-unlocalized-strings -- internal HTTP plumbing, not user-facing copy */
          throw new Response("Not Found", { status: 404, statusText: "Not Found" });
        }}
        element={<ErrorPage />}
      />
    </Route>
  </Route>,
);

export const router = createBrowserRouter(routes);
